import {
  Box,
  Button,
  Checkbox,
  Chip,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
  Tooltip
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { COLORS } from "colors/color";
import React, { useEffect, useRef, useState } from "react";
import { FieldTypography } from "StyledTypography/StyledTypography";
import CloseIcon from "@mui/icons-material/Close";
import { CancelButton } from "components/Buttons/Buttons";
import { NextButton } from "components/Buttons/Buttons";
import ImageUploadButton from "views/ImageUploadButton";
import SalaryInputWithCurrency from "components/Inputs/SalaryInputWithCurrency";
import GlobalCityAutocomplete from "components/CitiesPicker/CitiesPicker";
import TextEditor from "components/TextFields/EditorTextField";
import { NewCancelButton } from "components/Buttons/Buttons";
import { NewCreateOfferButton } from "components/Buttons/Buttons";
import { useTranslation } from "react-i18next";
import MatchingCoefficients, { MatchingCoefficientsDialog } from "./MatchingCoefficients";
import CostumizedQuiz from "./CostumizedQuiz";
import { SettingsIcon } from "lucide-react";

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "15px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
  },
});

const StyledDialogContent = styled(DialogContent)({
  paddingX: 19,
});
const StyledFormControl = styled(FormControl)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '& fieldset': {
      borderColor: '#e0e0e0',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: '#0017E7',
      borderWidth: '1px',
    },
    '&:hover': {
      transition: '0.3s ease',
      boxShadow: '0px 4px 20px rgba(0, 23, 231, 0.1)',
    },
    '&.Mui-focused': {
      boxShadow: '0px 4px 20px rgba(0, 23, 231, 0.1)',
      "& fieldset": {
        borderColor: "#0017E7",
        borderWidth: "2px",
      },
    },
    '&.Mui-error': {
      '&:hover, &.Mui-focused': {
        boxShadow: 'none',
        borderColor: '#d32f2f',
      },
    },
    // Ensure border remains visible when value is selected
    '& .MuiSelect-select': {
      borderColor: 'inherit',
    },
  },
});
const StyledChip = styled(Chip)(({ theme }) => ({
  borderRadius: "16px",
  "&.MuiChip-outlined": {
    borderColor: "#00000033",
  },
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    marginLeft: "20px",
    width: "250px",
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e0e0e0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      transition: "0.3s ease",
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0085FF",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0085FF",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "8px 14px",
    },
  },
}));
function WorkingDetailsDialog({
  setOpen2,
  setOpen,
  open2,
  errors,
  handleImageUpload,
  setWorkingMode,
  workingMode,
  location,
  setLocation,
  setErrors,
  numPeople,
  setNumPeople,
  salary,
  handleSalaryChange,
  currency,
  setCurrency,
  isChecked,
  handleCheckboxChange,
  isCheckedScr,
  handleCheckboxChangeScr,
  handleSave,
  experience,
  setExperience,
  diploma,
  setDiploma,
  otherDiploma,
  setOtherDiploma,
  fieldOfStudy, setFieldOfStudy,
  otherFieldOfStudy, setOtherFieldOfStudy,
  onNormalizedCoefficientsChange,
  onQuizChange
}) {

  const [showOtherFieldInput, setShowOtherFieldInput] = useState(false);

  const [showOtherDiplomaInput, setShowOtherDiplomaInput] = useState(false);

  const { t } = useTranslation();

  const handleDiplomaChange = (event) => {
    const value = event.target.value;
    setDiploma(value);
    setShowOtherDiplomaInput(value === "other");
    if (value !== "other") {
      setOtherDiploma("");
    }
  };
  const handleFieldOfStudyChange = (event) => {
    const value = event.target.value;
    setFieldOfStudy(value);
    setShowOtherFieldInput(value === "other");
    if (value !== "other") {
      setOtherFieldOfStudy("");
    }
  };
  const [coefficientsDropdownOpen, setCoefficientsDropdownOpen] = useState(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [coefficients, setCoefficients] = useState({
    profile_match: 0.20,
    skills_match: 0.30,
    experience_match: 0.35,
    education_match: 0.15
  });
  const [normaliseCoefficients, setNormaliseCoefficients] = useState({
    "profile_match": "0.200",
    "skills_match": "0.300",
    "experience_match": "0.210",
    "experience_years_match": "0.140",
    "education_match": "0.150"
  });
  const previousCoefficientsRef = useRef();
  const dropdownRef = useRef(null);
  const buttonsRef = useRef(null);
  const round2 = (num) => num.toFixed(3);

  useEffect(() => {
    if (coefficients) {
      // Calculate the total of the main coefficients
      const coefficientsTotal = coefficients.profile_match + coefficients.skills_match + coefficients.experience_match + coefficients.education_match;

      // If total is not 1, adjust the experience_match coefficient
      let adjustedCoefficients = { ...coefficients };
      if (Math.abs(coefficientsTotal - 1.0) > 0.01) {
        const difference = 1.0 - coefficientsTotal;
        adjustedCoefficients.experience_match += difference;
        // Ensure experience_match doesn't go below 0
        if (adjustedCoefficients.experience_match < 0) {
          adjustedCoefficients.experience_match = 0;
        }
      }

      const coefficientState = {
        "profile_match": round2(adjustedCoefficients.profile_match),
        "skills_match": round2(adjustedCoefficients.skills_match),
        "experience_match": round2(adjustedCoefficients.experience_match * 0.6),
        "experience_years_match": round2(adjustedCoefficients.experience_match * 0.4),
        "education_match": round2(adjustedCoefficients.education_match)
      };

      // Only update state if the values have actually changed
      const previousCoefficients = previousCoefficientsRef.current;
      const currentCoefficientsStr = JSON.stringify(coefficientState);
      const previousCoefficientsStr = previousCoefficients ? JSON.stringify(previousCoefficients) : null;

      if (currentCoefficientsStr !== previousCoefficientsStr) {
        setNormaliseCoefficients(coefficientState);
        previousCoefficientsRef.current = coefficientState;
      }

      // Call the callback with the normalized coefficients if provided
      if (onNormalizedCoefficientsChange) {
        onNormalizedCoefficientsChange(coefficientState);
      }
    }
  }, [coefficients, onNormalizedCoefficientsChange]);

  const handleSaveCoefficients = (newWeights) => {
    // Format the coefficients in the required JSON format
    const formattedCoefficients = {
      "profile_match": newWeights.profile_match,
      "skills_match": newWeights.skills_match,
      "experience_match": newWeights.experience_match,
      "education_match": newWeights.education_match
    };

    setCoefficients(newWeights);
    console.log('Saved coefficients:', formattedCoefficients);
  };

  const handleCreateOffer = async () => {
    try {
      console.log('🚀 Starting post creation...');
      await handleSave();
      console.log('✅ Post creation completed (refresh will be handled by parent component)');
    } catch (error) {
      console.error("❌ Error creating post:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the toggle buttons
      if (buttonsRef.current && buttonsRef.current.contains(event.target)) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCoefficientsDropdownOpen(false);
      }
    };

    if (coefficientsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [coefficientsDropdownOpen]);

  const total = coefficients ? Object.values(coefficients).reduce((sum, val) => sum + val, 0) : 0;
  const isCoefficientsValid = Math.abs(total - 1.0) < 0.01;

  return (
    <StyledDialog
      open={open2}
      onClose={() => setOpen2(false)}
      fullWidth
      maxWidth="sm"
    >
      <Box
        sx={{
          width: "100%",
          height: 60,
          backgroundColor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: ' 1px solid #D9D9D9',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "#000",
          }}
        >
          {t('workingDetailsDialog.addNewPost', 'Add new post')}
        </DialogTitle>
        <IconButton
          onClick={() => setOpen2(false)}
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            mr: 3,
            color: "#000",
            border: '1px solid #D9D9D9',
            "&:hover": {
              color: '#000',
              backgroundColor: '#0084ff08',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <StyledDialogContent>
        <Typography
          sx={{
            color: "#004F98",
            fontSize: 15,
            fontFamily: "Poppins",
            fontWeight: "600",
          }}
        >
          {t('workingDetailsDialog.workDetails', 'Work details')}
        </Typography>
        <Stack spacing={1}>
          <ImageUploadButton
            onImageUpload={handleImageUpload}
            error={!!errors.postImage}
            helperText={errors.postImage}
            required
          />

          <Grid container spacing={1.5}>
            {/* Number of Experience */}
            <Grid item xs={12} md={4}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.numberOfExperience', 'Number of Experience')}*</FieldTypography>
              <StyledFormControl fullWidth error={!!errors.numPeople}>
                <Select
                  labelId="number-of-experience-label"
                  value={numPeople}
                  onChange={(event) => {
                    setNumPeople(event.target.value);
                    setErrors((prev) => ({ ...prev, numPeople: "" }));
                  }}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  }}
                >
                  <MenuItem value={1}>1 an</MenuItem>
                  {[...Array(8).keys()].map((num) => (
                    <MenuItem key={num + 2} value={num + 2}>
                      {num + 2} ans
                    </MenuItem>
                  ))}
                  <MenuItem value={10}>plus de 9 ans</MenuItem>
                </Select>
                {errors.numPeople && (
                  <FormHelperText error sx={{ fontSize: "12px" }}>{errors.numPeople}</FormHelperText>
                )}
              </StyledFormControl>
            </Grid>

            {/* Working Mode */}
            <Grid item xs={12} md={4}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.workingMode', 'Working mode')}*</FieldTypography>
              <StyledFormControl fullWidth error={!!errors.workingMode}>
                <Select
                  labelId="working-mode-label"
                  value={workingMode || ""}
                  onChange={(event) => {
                    setWorkingMode(event.target.value);
                    setErrors((prev) => ({ ...prev, workingMode: "" }));
                  }}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  }}
                >
                  <MenuItem value="Remote">{t('workingDetailsDialog.mode.remote', 'Remote')}</MenuItem>
                  <MenuItem value="On site">{t('workingDetailsDialog.mode.onsite', 'On site')}</MenuItem>
                  <MenuItem value="Hybrid">{t('workingDetailsDialog.mode.hybrid', 'Hybrid')}</MenuItem>
                </Select>
                {errors.workingMode && (
                  <FormHelperText error sx={{ fontSize: "12px" }}>{errors.workingMode}</FormHelperText>
                )}
              </StyledFormControl>
            </Grid>

            {/* LinkedIn Checkbox */}
            <Grid item xs={12} md={4}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>&nbsp;</FieldTypography>
              <Box sx={{
                height: "40px",
                display: "flex",
                alignItems: "center",
                pl: 1,
                pr: 1
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      color="primary"
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                        p: 0,
                        mr: 1,
                      }}
                    />
                  }
                  label={
                    <Typography sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px"
                    }}>
                      {t('workingDetailsDialog.makePostOnLinkedIn', 'Make a post on LinkedIn')}
                    </Typography>
                  }
                  sx={{
                    mr: 0,
                    p: 0,
                    alignItems: 'center',
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            {/* Highest Diploma */}
            <Grid item xs={12} md={6}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.highestDiploma', 'Highest Diploma')}*</FieldTypography>
              <StyledFormControl fullWidth>
                <Select
                  labelId="diplom-label"
                  id="diplom"
                  name="diplom"
                  value={diploma}
                  onChange={handleDiplomaChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  }}
                >
                  <MenuItem value="High School Diploma">{t('workingDetailsDialog.diploma.highSchool', "High School Diploma")}</MenuItem>
                  <MenuItem value="Associate's Degree">{t('workingDetailsDialog.diploma.associate', "Associate's Degree")}</MenuItem>
                  <MenuItem value="Bachelor's Degree">{t('workingDetailsDialog.diploma.bachelor', "Bachelor's Degree")}</MenuItem>
                  <MenuItem value="Master's Degree">{t('workingDetailsDialog.diploma.master', "Master's Degree")}</MenuItem>
                  <MenuItem value="Ph.D.">{t('workingDetailsDialog.diploma.phd', "Ph.D.")}</MenuItem>
                  <MenuItem value="MBA">{t('workingDetailsDialog.diploma.mba', "MBA")}</MenuItem>
                  <MenuItem value="Professional Certification">{t('workingDetailsDialog.diploma.professionalCertification', "Professional Certification")}</MenuItem>
                  <MenuItem value="Vocational Diploma">{t('workingDetailsDialog.diploma.vocational', "Vocational Diploma")}</MenuItem>
                  <MenuItem value="Technical Diploma">{t('workingDetailsDialog.diploma.technical', "Technical Diploma")}</MenuItem>
                  <MenuItem value="other">{t('workingDetailsDialog.diploma.other', "Other")}</MenuItem>
                </Select>
              </StyledFormControl>
              {showOtherDiplomaInput && (
                <StyledTextField
                  fullWidth
                  placeholder={t('workingDetailsDialog.pleaseSpecifyDiploma', 'Please specify your diploma')}
                  value={otherDiploma}
                  onChange={(e) => setOtherDiploma(e.target.value)}
                  sx={{ mt: 0.5, height: "40px" }}
                />
              )}
            </Grid>

            {/* Field of Study */}
            <Grid item xs={12} md={6}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.fieldOfStudy', 'Field of Study')}*</FieldTypography>
              <StyledFormControl fullWidth>
                <Select
                  labelId="field-of-study-label"
                  id="fieldOfStudy"
                  name="fieldOfStudy"
                  value={fieldOfStudy}
                  onChange={handleFieldOfStudyChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  }}
                >
                  <MenuItem value="Computer Science">{t('workingDetailsDialog.field.computerScience', 'Computer Science')}</MenuItem>
                  <MenuItem value="Software Engineering">{t('workingDetailsDialog.field.softwareEngineering', 'Software Engineering')}</MenuItem>
                  <MenuItem value="Information Technology">{t('workingDetailsDialog.field.informationTechnology', 'Information Technology')}</MenuItem>
                  <MenuItem value="Data Science">{t('workingDetailsDialog.field.dataScience', 'Data Science')}</MenuItem>
                  <MenuItem value="Mechanical Engineering">{t('workingDetailsDialog.field.mechanicalEngineering', 'Mechanical Engineering')}</MenuItem>
                  <MenuItem value="Electrical Engineering">{t('workingDetailsDialog.field.electricalEngineering', 'Electrical Engineering')}</MenuItem>
                  <MenuItem value="Civil Engineering">{t('workingDetailsDialog.field.civilEngineering', 'Civil Engineering')}</MenuItem>
                  <MenuItem value="Business Administration">{t('workingDetailsDialog.field.businessAdministration', 'Business Administration')}</MenuItem>
                  <MenuItem value="Finance">{t('workingDetailsDialog.field.finance', 'Finance')}</MenuItem>
                  <MenuItem value="Marketing">{t('workingDetailsDialog.field.marketing', 'Marketing')}</MenuItem>
                  <MenuItem value="Accounting">{t('workingDetailsDialog.field.accounting', 'Accounting')}</MenuItem>
                  <MenuItem value="Economics">{t('workingDetailsDialog.field.economics', 'Economics')}</MenuItem>
                  <MenuItem value="Mathematics">{t('workingDetailsDialog.field.mathematics', 'Mathematics')}</MenuItem>
                  <MenuItem value="Physics">{t('workingDetailsDialog.field.physics', 'Physics')}</MenuItem>
                  <MenuItem value="Chemistry">{t('workingDetailsDialog.field.chemistry', 'Chemistry')}</MenuItem>
                  <MenuItem value="Biology">{t('workingDetailsDialog.field.biology', 'Biology')}</MenuItem>
                  <MenuItem value="medicine">{t('workingDetailsDialog.field.medicine', 'Medicine')}</MenuItem>
                  <MenuItem value="nursing">{t('workingDetailsDialog.field.nursing', 'Nursing')}</MenuItem>
                  <MenuItem value="pharmacy">{t('workingDetailsDialog.field.pharmacy', 'Pharmacy')}</MenuItem>
                  <MenuItem value="psychology">{t('workingDetailsDialog.field.psychology', 'Psychology')}</MenuItem>
                  <MenuItem value="sociology">{t('workingDetailsDialog.field.sociology', 'Sociology')}</MenuItem>
                  <MenuItem value="political_science">{t('workingDetailsDialog.field.politicalScience', 'Political Science')}</MenuItem>
                  <MenuItem value="history">{t('workingDetailsDialog.field.history', 'History')}</MenuItem>
                  <MenuItem value="languages">{t('workingDetailsDialog.field.languages', 'Languages')}</MenuItem>
                  <MenuItem value="graphic_design">{t('workingDetailsDialog.field.graphicDesign', 'Graphic Design')}</MenuItem>
                  <MenuItem value="architecture">{t('workingDetailsDialog.field.architecture', 'Architecture')}</MenuItem>
                  <MenuItem value="fine_arts">{t('workingDetailsDialog.field.fineArts', 'Fine Arts')}</MenuItem>
                  <MenuItem value="other">{t('workingDetailsDialog.field.other', 'Other')}</MenuItem>
                </Select>
              </StyledFormControl>
              {showOtherFieldInput && (
                <StyledTextField
                  fullWidth
                  placeholder={t('workingDetailsDialog.pleaseSpecifyField', 'Please specify your field of study')}
                  value={otherFieldOfStudy}
                  onChange={(e) => setOtherFieldOfStudy(e.target.value)}
                  sx={{ mt: 0.5, height: "40px" }}
                />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            {/* Location */}
            <Grid item xs={12} md={6}>
              <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.location', 'Location')}*</FieldTypography>
              <GlobalCityAutocomplete
                sx={{ borderColor: "#e0e0e0" }}
                value={location}
                onChange={(newValue) => {
                  setLocation(newValue);
                  setErrors((prev) => ({ ...prev, location: "" }));
                }}
                error={!!errors.location}
                helperText={errors.location}
                textFieldProps={{
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  },
                }}
              />
            </Grid>


            {/* Salary */}
            <Grid item xs={12} md={6}>
              {/* <FieldTypography sx={{ fontSize: "13px", mb: 0.5 }}>{t('workingDetailsDialog.salary', 'Salary')}</FieldTypography> */}
              <SalaryInputWithCurrency
                salary={salary}
                setSalary={handleSalaryChange}
                currency={currency}
                setCurrency={setCurrency}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    mt: -0.4
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box ref={buttonsRef} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  component="span"
                  color={"primary"}
                  onClick={() => {
                    setCoefficientsDropdownOpen(!coefficientsDropdownOpen);
                    setQuizDialogOpen(false);
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                >
                  Modifier les paramètres de matching
                </Typography>

                {/* Quiz Button */}
                <Typography
                  component="span"
                  color={"primary"}
                  onClick={() => {
                    setQuizDialogOpen(!quizDialogOpen);
                    setCoefficientsDropdownOpen(false);
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "inline-flex",
                    mr: 3,
                    alignItems: "center",
                    "&:hover": {
                      textDecoration: "underline"
                    }
                  }}
                >
                  Ajouter un Quiz
                </Typography>
              </Box>

              {/* Coefficients Dropdown - Full Width */}
              <Box sx={{ position: 'relative' }} ref={dropdownRef}>
                {coefficientsDropdownOpen && (
                  <Box
                    sx={{
                      position: 'relative',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      mt: 1,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '10px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      boxShadow: 'none'
                    }}
                  >
                    <Box p={0}>
                      <MatchingCoefficients
                        initialWeights={coefficients}
                        onWeightsChange={(newWeights) => setCoefficients(newWeights)}
                      />

                    </Box>
                  </Box>
                )}
              </Box>

              {/* Quiz Dropdown - Full Width */}
              {quizDialogOpen && (
                <Box
                  sx={{
                    position: 'relative',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    mt: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    boxShadow: 'none'
                  }}
                >
                  <CostumizedQuiz
                    initialQuestions={quizQuestions}
                    onQuestionsChange={(questions) => {
                      console.log('Quiz Questions Updated:', JSON.stringify({ questions }, null, 2));
                      // Store quiz questions in parent state to persist across dropdown open/close
                      setQuizQuestions(questions);
                      // Pass quiz data to parent - null if no questions
                      if (onQuizChange) {
                        onQuizChange(questions.length > 0 ? { questions } : null);
                      }
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Stack>
      </StyledDialogContent>
      <DialogActions
        sx={{
          pr: 3,
          pb: 2,
        }}
      >
        <NewCancelButton
          onClick={() => {
            setOpen(true);
            setOpen2(false);
          }}
        >
          {t('workingDetailsDialog.previous', 'Previous')}
        </NewCancelButton>
        <Tooltip
          title={!isCoefficientsValid ? "Veuillez équilibrer les coefficients (total doit être 100%)" : ""}
          placement="top"
        >
          <span>
            <NewCreateOfferButton
              onClick={handleCreateOffer}
              disabled={!isCoefficientsValid}
              sx={!isCoefficientsValid ? { opacity: 0.5 } : {}}
            >
              {t('workingDetailsDialog.createOffer', 'Create offer')}
            </NewCreateOfferButton>
          </span>
        </Tooltip>
      </DialogActions>


    </StyledDialog>
  );
}

export default WorkingDetailsDialog;
